import { countConditions } from "@estelle/domain";

import { apiFetch } from "@/adapters/api/base";
import type {
  CustomerAnnotations,
  CustomersRepository,
  CustomerSummary,
  CustomerWithDetails,
  Id,
  ListQuery,
  Page,
  WriteResult,
} from "@estelle/domain";

/**
 * Customers, over HTTP (#447).
 *
 * Reads are Postgres over the mirror; the three writes touch only the ERP's
 * own note and tags — nothing here reaches Shopify, and no route modifies a
 * mirrored customer.
 *
 * What comes back depends on `customers.view_contact`. This adapter passes the
 * body through untouched: a summary without a `phone` key is the server
 * withholding it, and adding one here would be inventing what it never sent.
 */

const BASE = "/api/customers";

function listParams(query: ListQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.limit !== undefined) params.set("limit", String(query.limit));
  if (query.cursor !== undefined) params.set("cursor", query.cursor);
  if (query.search !== undefined && query.search !== "") {
    params.set("search", query.search);
  }
  if (query.filter && countConditions(query.filter) > 0) {
    params.set("filter", JSON.stringify(query.filter));
  }
  if (query.sort && query.sort.length > 0) {
    params.set(
      "sort",
      query.sort.map((entry) => `${entry.column}:${entry.direction}`).join(","),
    );
  }
  return params;
}

/**
 * A write's outcome. A refusal arrives as a `WriteResult` body with a 4xx
 * status — a rejection to show, not a failure to throw. Anything else is a
 * transport failure, which does throw.
 */
async function write(
  path: string,
  method: "POST" | "PATCH",
  body: unknown,
): Promise<WriteResult<CustomerAnnotations>> {
  const response = await apiFetch(`${BASE}${path}`, {
    method,
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  const result = (await response
    .json()
    .catch(() => null)) as WriteResult<CustomerAnnotations> | null;
  if (result !== null && typeof result === "object" && "ok" in result) {
    return result;
  }
  // A 403 from `requirePermission` is `{ error, permission }`, not a result.
  if (response.status === 403) {
    const refusal = result as { permission?: string } | null;
    return {
      ok: false,
      rejection: {
        kind: "forbidden",
        message: "You do not have permission to do that.",
        permission: refusal?.permission ?? "customers.annotate",
      },
    };
  }
  throw new Error(
    `Customers ${method} failed: ${String(response.status)} on ${path}`,
  );
}

export function createApiCustomers(): CustomersRepository {
  return {
    list: async (query) => {
      const params = listParams(query).toString();
      const response = await apiFetch(`${BASE}${params ? `?${params}` : ""}`);
      if (!response.ok) {
        throw new Error(`Customers request failed: ${String(response.status)}`);
      }
      return (await response.json()) as Page<CustomerSummary>;
    },

    get: async (id: Id): Promise<CustomerWithDetails | null> => {
      const response = await apiFetch(`${BASE}/${encodeURIComponent(id)}`);
      if (response.status === 404) return null;
      if (!response.ok) {
        throw new Error(`Customers request failed: ${String(response.status)}`);
      }
      return (await response.json()) as CustomerWithDetails;
    },

    setNote: (id, note) =>
      write(`/${encodeURIComponent(id)}/note`, "PATCH", { note }),

    addTag: (id, tag) =>
      write(`/${encodeURIComponent(id)}/tags`, "POST", { tag }),

    removeTag: (id, tag) =>
      write(`/${encodeURIComponent(id)}/tags/remove`, "POST", { tag }),
  };
}
